import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, ArrowRightIcon } from 'lucide-react';
import { blogPosts, Category } from '../data/blogPosts';
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-421">
      {children}
    </motion.div>;
}
const categoryColors: Record<Category, string> = {
  Todos: 'bg-accent/10 text-accent border-accent/20',
  'Para Agentes': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Para Agencias': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  Herramientas: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  Estrategia: 'bg-accent/10 text-accent border-accent/20'
};
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = blogPosts.find(p => p.slug === slug);
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Reclutas Digitales`;
    } else {
      document.title = 'Artículo no encontrado | Reclutas Digitales';
    }
  }, [post]);
  if (!post) {
    return <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center px-4 text-center" data-id="element-422">
        <h1 className="text-3xl sm:text-4xl font-bold text-cream mb-4" data-id="element-423">
          Artículo no encontrado
        </h1>
        <p className="text-[#b8b4ae] mb-8" data-id="element-424">
          El recurso que buscas no existe o ha sido movido.
        </p>
        <Link to="/recursos" className="inline-flex items-center gap-2 px-6 py-3 bg-surface border border-border-subtle hover:border-accent/50 text-cream rounded-xl transition-colors" data-id="element-425">
          <ArrowLeftIcon size={16} data-id="element-426" />
          Volver a Recursos
        </Link>
      </div>;
  }
  return <div className="bg-charcoal min-h-screen pb-24" data-id="element-427">
      {/* Article Header */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 border-b border-border-subtle bg-surface/30" data-id="element-428">
        <div className="max-w-3xl mx-auto" data-id="element-429">
          <FadeInSection data-id="element-430">
            <Link to="/recursos" className="inline-flex items-center gap-2 text-sm text-[#7a7672] hover:text-accent transition-colors mb-8" data-id="element-431">
              <ArrowLeftIcon size={14} data-id="element-432" />
              Volver a todos los recursos
            </Link>

            <div className="mb-6" data-id="element-433">
              <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold border ${categoryColors[post.category]}`} data-id="element-434">
                {post.category}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-435">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-sm text-[#b8b4ae]" data-id="element-436">
              <span className="flex items-center gap-2" data-id="element-437">
                <CalendarIcon size={16} className="text-[#7a7672]" data-id="element-438" />
                {post.publishDate}
              </span>
              <span className="flex items-center gap-2" data-id="element-439">
                <ClockIcon size={16} className="text-[#7a7672]" data-id="element-440" />
                {post.readTime} de lectura
              </span>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-441">
        <div className="max-w-3xl mx-auto" data-id="element-442">
          <FadeInSection delay={0.1} data-id="element-443">
            <div className="prose prose-invert prose-lg max-w-none" data-id="element-444">
              <p className="text-xl text-[#b8b4ae] leading-relaxed mb-10 font-medium" data-id="element-445">
                {post.description}
              </p>

              <div className="space-y-8 text-[#b8b4ae] leading-relaxed" data-id="element-446">
                {post.content.map((paragraph, index) => <p key={index} data-id="element-447">{paragraph}</p>)}
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pt-12 px-4 sm:px-6 lg:px-8" data-id="element-448">
        <div className="max-w-3xl mx-auto" data-id="element-449">
          <FadeInSection delay={0.2} data-id="element-450">
            <div className="bg-elevated border border-accent/20 rounded-3xl p-8 sm:p-12 text-center" data-id="element-451">
              <h2 className="text-2xl font-bold text-cream mb-4" data-id="element-452">
                ¿Listo para implementar este sistema?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed max-w-xl mx-auto" data-id="element-453">
                Deja de intentar descifrarlo por tu cuenta. Agenda un
                diagnóstico y te mostraremos cómo instalar esta infraestructura
                en tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-454">
                <Link to="/contact-us" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200" data-id="element-455">
                  Agendar diagnóstico
                  <ArrowRightIcon size={18} data-id="element-456" />
                </Link>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}